import { v4 as uuid } from 'uuid';

import { type Period } from '@atlassian/eoc-schedule-common';

import { type RotationDetail } from '../../../common/types';
import {
	type CalendarLayer,
	type PeriodResponse,
	type RotationPayload,
} from '../../../services/endpoints/schedule/types';
import { isEmpty } from '../../../utils/validators';
import { type Week } from '../state/types';

const fromPeriodResponseToPeriod = (period: PeriodResponse): Period => {
	const {
		startTimeUtcMillis,
		endTimeUtcMillis,
		responder,
		periodType,
		alias,
		isTrueStartTime,
		isTrueEndTime,
		periodStartOffsetMillis,
		periodEndOffsetMillis,
		from,
	} = period;

	return {
		id: uuid(),
		responder: {
			id: responder?.id,
			type: responder.type,
			deleted: responder?.deleted ?? false,
		},
		startEpoch: startTimeUtcMillis,
		endEpoch: endTimeUtcMillis,
		type: periodType,
		alias,
		isTrueStartTime,
		isTrueEndTime,
		startOffset: periodStartOffsetMillis,
		endOffset: periodEndOffsetMillis,
		from: from
			? {
					id: from?.id,
					type: from.type,
					deleted: from?.deleted ?? false,
				}
			: undefined,
	};
};

const fromRotationDetailToRotationPayload = (rotation: RotationDetail): RotationPayload => {
	const {
		id,
		name,
		responders,
		startTimeRaw,
		endTimeRaw,
		rotationType,
		timeTemplateId,
		rotationPeriod,
	} = rotation;

	const payload = {
		id,
		name,
		responders,
		rotationType,
		rotationPeriod,
		startTimeRaw,
	} as RotationPayload;

	if (isEmpty(endTimeRaw)) {
		payload.hasEndTime = false;
	} else {
		payload.hasEndTime = true;
		payload.endTimeRaw = endTimeRaw;
	}

	if (isEmpty(timeTemplateId)) {
		payload.restrictions = rotation.restrictions;
		payload.timingType = rotation.timingType;
	} else {
		payload.timeTemplateId = timeTemplateId;
	}

	return payload;
};

const fromCalendarLayerWeekToWeek = (weeks: CalendarLayer['weeks']): Week[] => {
	return weeks.map(({ rotations, relativeEndTime, relativeStartTime }) => ({
		startEpoch: relativeStartTime,
		endEpoch: relativeEndTime,
		rotations: rotations.map(({ id, name, periods, hasDeletedResponders }) => ({
			id,
			name,
			hasDeletedResponders,
			periods: periods.map((period) => fromPeriodResponseToPeriod(period)),
		})),
	}));
};

export {
	fromPeriodResponseToPeriod,
	fromRotationDetailToRotationPayload,
	fromCalendarLayerWeekToWeek,
};
